import {
  Card,
  List,
  ListItem,
  ListItemText,
  PageHeaderWrapper,
  Text,
  Title,
} from "@clipboard-health/ui-components";
import { CardContent, DialogContent, Stack } from "@mui/material";
import { ConsequencePill } from "@src/appV2/redesign/ClipboardScore/components/Activity/ConsequencePill";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useWorkBadgeSettings } from "@src/appV2/redesign/WorkBadges/hooks/useWorkBadgeSettings";

export interface BadgeVerificationIntroContentProps {
  onBack: () => void;
  onTakePhoto: () => void;
  onNoBadge: () => void;
  onWhyBadgeRequired?: () => void;
}

export function BadgeVerificationIntroContent(props: BadgeVerificationIntroContentProps) {
  const { onBack, onTakePhoto, onNoBadge, onWhyBadgeRequired } = props;
  const { workBadgeVerificationPassedScore, workBadgeVerificationFailedOrSkippedScore } =
    useWorkBadgeSettings();

  return (
    <>
      <PageHeaderWrapper variant="tertiary">
        <PageHeaderTopRow onClickBack={onBack} />
      </PageHeaderWrapper>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack alignItems="center" textAlign="center">
          <Title semibold variant="h4" component="h1">
            To start your shift, show that you have your badge
          </Title>

          <Text variant="h6" color={(theme) => theme.palette.text.secondary} marginTop={4}>
            You&apos;ll clock in after submitting a photo of your badge on your work attire
          </Text>

          <Stack alignSelf="stretch" sx={{ marginTop: 8 }}>
            <Card variant="primary">
              <CardContent>
                <Text semibold variant="h6" align="left">
                  Remember: wearing a badge on shift impacts your score
                </Text>
                <List disablePadding sx={{ marginTop: 4 }}>
                  <ListItem
                    sx={{ paddingY: 4 }}
                    secondaryAction={
                      <ConsequencePill
                        consequence={workBadgeVerificationPassedScore}
                        type="bonus"
                      />
                    }
                  >
                    <ListItemText
                      disableTypography
                      primary={<Text variant="body1">Wearing a badge on shift</Text>}
                    />
                  </ListItem>
                  <ListItem
                    sx={{ paddingY: 4 }}
                    secondaryAction={
                      <ConsequencePill
                        consequence={workBadgeVerificationFailedOrSkippedScore}
                        type="penalty"
                      />
                    }
                  >
                    <ListItemText
                      disableTypography
                      primary={<Text variant="body1">Not wearing a badge on shift</Text>}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
            {/* paddingX matches CardContent default padding for visual alignment */}
            <Text
              variant="body2"
              color={(theme) => theme.palette.text.secondary}
              marginTop={3}
              align="left"
              paddingX={5}
            >
              Your score will change after the end of your shift
            </Text>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <Button fullWidth variant="contained" size="large" onClick={onTakePhoto}>
          Take a photo
        </Button>
        <Button fullWidth variant="outlined" size="large" onClick={onNoBadge}>
          I don&apos;t have my badge with me
        </Button>
        {onWhyBadgeRequired && (
          <TextButton size="large" onClick={onWhyBadgeRequired}>
            Why is a badge required?
          </TextButton>
        )}
      </DialogFooter>
    </>
  );
}
